import { useGlobalMutation } from 'state/query-client/hooks';
import { deleteIntegration } from 'services/integration-service';
import { useConfig } from './use-config';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';

/**
 * Custom hook to delete integrations
 *
 * This hook provides a mutation function to delete integrations when users
 * disconnect their external services. It uses the global mutation pattern
 * for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - TypeScript support for request parameters
 *
 * @returns {Object} The mutation object with methods and state
 * @returns {Function} mutate - Function to trigger the integration deletion
 * @returns {Function} mutateAsync - Async function to trigger the integration deletion
 * @returns {boolean} isPending - Whether the mutation is in progress
 * @returns {boolean} isSuccess - Whether the mutation completed successfully
 * @returns {boolean} isError - Whether the mutation failed
 * @returns {Error | null} error - The error if mutation failed
 *
 * @example
 * const { mutate: deleteIntegration, isPending } = useDeleteIntegration();
 * 
 * const handleDisconnect = (connectionId) => {
 *   deleteIntegration(connectionId, {
 *     onSuccess: () => {
 *       console.log('Integration deleted successfully');
 *       // Refetch integrations or update UI
 *     },
 *     onError: (error) => {
 *       console.error('Failed to delete integration:', error);
 *     }
 *   });
 * };
 */
interface DeleteIntegrationParams {
    connectionId: string;
    integrationType?: string;
}

export const useDeleteIntegration = () => {
    const { configurationSettings } = useConfig();
    const { removeIntegration } = useIntegrationsStore();

    return useGlobalMutation<void, Error, string | DeleteIntegrationParams>({
        mutationFn: async (params: string | DeleteIntegrationParams) => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            
            // Support both old string format and new object format for backward compatibility
            const connectionId = typeof params === 'string' ? params : params.connectionId;
            const integrationType = typeof params === 'string' ? undefined : params.integrationType;
            
            await deleteIntegration(configurationSettings, connectionId, integrationType);
            
            // Only remove from store if it's a hard delete (integrationType === 'sftp')
            // For soft delete, the integration should remain in store with is_active: false
            if (integrationType === 'sftp') {
                removeIntegration(connectionId);
            }
            // For other integrations (or when integrationType is undefined), 
            // we still remove from store for backward compatibility
            else if (typeof params === 'string') {
                removeIntegration(connectionId);
            }
        },
    });
};
